import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Box,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import CustomTextField from "../components/CustomTextField";
import {
  AUTH_FIELD_BG,
  AuthFieldLabel,
  AuthLink,
  AuthPage,
  AuthStagger,
  authSubmitSx,
  useAuthNav,
} from "../components/auth/AuthLayout";
import { toast } from "react-toastify";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  requestPasswordResetCode,
  verifyPasswordResetCode,
  resetPasswordWithCode,
} from "../services/authService";

const CODE_LENGTH = 6;
type CodeInputElement = HTMLInputElement | HTMLDivElement;

const ForgotPassword: React.FC = () => {
  const { go } = useAuthNav();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [loadingAction, setLoadingAction] = useState<
    "send" | "verify" | "reset" | "resend" | null
  >(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (step !== 2 || resendCooldown <= 0) return;
    const timerId = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerId);
  }, [step, resendCooldown]);

  const focusCodeInput = (index: number) => {
    const input = codeInputsRef.current[index];
    if (input) {
      input.focus();
      input.setSelectionRange?.(input.value.length, input.value.length);
    }
  };

  const handleCodeInput = (rawValue: string, index: number) => {
    const sanitized = rawValue.replace(/\D/g, "");
    const codeArray = Array.from(
      { length: CODE_LENGTH },
      (_, i) => code[i] || ""
    );

    if (sanitized.length === 0) {
      const hadValue = !!code[index];
      codeArray[index] = "";
      setCode(codeArray.join(""));
      if (!hadValue && index > 0) focusCodeInput(index - 1);
      return;
    }

    let nextIndex = index;
    sanitized.split("").forEach((char) => {
      if (nextIndex < CODE_LENGTH) {
        codeArray[nextIndex] = char;
        nextIndex += 1;
      }
    });

    setCode(codeArray.join(""));

    if (nextIndex < CODE_LENGTH) {
      focusCodeInput(nextIndex);
    } else {
      codeInputsRef.current[CODE_LENGTH - 1]?.blur();
    }
  };

  const handleCodePaste = (
    event: React.ClipboardEvent<CodeInputElement>,
    index: number
  ) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text");
    if (!pasted) return;
    handleCodeInput(pasted, index);
  };

  const handleCodeKeyDown = (
    event: React.KeyboardEvent<CodeInputElement>,
    index: number
  ) => {
    if (event.key === "Backspace" && !code[index] && index > 0) {
      focusCodeInput(index - 1);
    }
  };

  const handleSendCode = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Please enter your email");
      return;
    }
    setError("");
    setStatusMessage("");
    setLoadingAction("send");
    try {
      const response = await requestPasswordResetCode(trimmedEmail);
      if (!response.success) {
        setError(response.error || "Failed to send verification code");
        return;
      }
      setEmail(trimmedEmail);
      setCode("");
      setStatusMessage(response.message || "Verification code sent");
      setStep(2);
      setResendCooldown(60);
    } catch (sendError: any) {
      const apiErrorMessage =
        sendError?.response?.data?.message ||
        sendError?.response?.data?.error ||
        sendError?.message;
      setError(apiErrorMessage || "Failed to send verification code");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleResendCode = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Email is missing. Please enter your email again.");
      setStep(1);
      return;
    }
    setError("");
    setStatusMessage("");
    setLoadingAction("resend");
    try {
      const response = await requestPasswordResetCode(trimmedEmail);
      if (!response.success) {
        setError(response.error || "Failed to resend verification code");
        return;
      }
      setCode("");
      setStatusMessage(
        response.message ||
          "A new verification code was sent. Please use the most recent code."
      );
      setResendCooldown(60);
      setStep(2);
    } catch (resendError: any) {
      const apiErrorMessage =
        resendError?.response?.data?.message ||
        resendError?.response?.data?.error ||
        resendError?.message;
      setError(apiErrorMessage || "Failed to resend verification code");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleVerifyCode = async () => {
    const trimmedEmail = email.trim();
    const trimmedCode = code.trim();
    if (!trimmedEmail) {
      setError("Email is missing. Please enter your email again.");
      setStep(1);
      return;
    }
    if (!trimmedCode || trimmedCode.length !== CODE_LENGTH) {
      setError("Please enter the complete verification code");
      return;
    }
    setError("");
    setStatusMessage("");
    setLoadingAction("verify");
    try {
      const response = await verifyPasswordResetCode(
        trimmedEmail,
        trimmedCode
      );
      if (!response.success) {
        setError(response.error || "Failed to verify code");
        return;
      }
      setCode(trimmedCode);
      setStatusMessage(
        response.message || "Code verified. You can now reset your password."
      );
      setStep(3);
    } catch (verifyError: any) {
      const apiErrorMessage =
        verifyError?.response?.data?.message ||
        verifyError?.response?.data?.error ||
        verifyError?.message;
      setError(apiErrorMessage || "Failed to verify code");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    const trimmedEmail = email.trim();
    const trimmedCode = code.trim();
    if (!trimmedEmail || !trimmedCode) {
      setError(
        "Email or verification code is missing. Please request a new code."
      );
      setStep(1);
      return;
    }
    setError("");
    setStatusMessage("");
    setLoadingAction("reset");
    try {
      const response = await resetPasswordWithCode(
        trimmedEmail,
        trimmedCode,
        newPassword,
        confirmPassword
      );
      if (!response.success) {
        setError(response.error || "Failed to reset password");
        return;
      }
      setStatusMessage(response.message || "Password reset successful!");
      setCode("");
      setNewPassword("");
      setConfirmPassword("");
      setStep(1);
      toast.success(response.message || "Password reset successful!");
      go("/login");
    } catch (resetError: any) {
      const apiErrorMessage =
        resetError?.response?.data?.message ||
        resetError?.response?.data?.error ||
        resetError?.message;
      setError(apiErrorMessage || "Failed to reset password");
    } finally {
      setLoadingAction(null);
    }
  };

  const spinner = (active: boolean) =>
    active ? <CircularProgress size={18} color="inherit" /> : undefined;
  const passwordToggle = (shown: boolean, toggle: () => void) => (
    <IconButton
      aria-label={shown ? "Hide password" : "Show password"}
      onClick={toggle}
      onMouseDown={(e) => e.preventDefault()}
      edge="end"
    >
      {shown ? <VisibilityOff /> : <Visibility />}
    </IconButton>
  );
  const heading =
    step === 1
      ? {
          title: "Reset your password",
          subtitle: "Enter your account email and we'll send you a 6-digit code.",
        }
      : step === 2
      ? {
          title: "Check your email",
          subtitle: `We sent a 6-digit code to ${email || "your email"}.`,
        }
      : {
          title: "Choose a new password",
          subtitle: "Pick a strong password you'll remember.",
        };

  return (
    <AuthPage
      title={heading.title}
      subtitle={heading.subtitle}
      stepKey={step}
      footer={
        <>
          Remembered it? <AuthLink to="/login">Back to log in</AuthLink>
        </>
      }
    >
      {step === 1 && (
        <AuthStagger startMs={120}>
          <AuthFieldLabel>Email</AuthFieldLabel>
          <CustomTextField
            label=""
            type="email"
            value={email}
            bgColor={AUTH_FIELD_BG}
            placeholder="you@company.com"
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            sx={{ mb: 3 }}
            error={!!error}
            helperText={error}
          />
          <Button
            variant="contained"
            fullWidth
            sx={authSubmitSx}
            onClick={handleSendCode}
            disabled={loadingAction === "send"}
            startIcon={spinner(loadingAction === "send")}
          >
            {loadingAction === "send" ? "Sending..." : "Send code"}
          </Button>
        </AuthStagger>
      )}
      {step === 2 && (
        <AuthStagger startMs={120}>
          <AuthFieldLabel>Verification code</AuthFieldLabel>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1, sm: 1.5 },
              mb: 2,
              justifyContent: "space-between",
            }}
          >
            {Array.from({ length: CODE_LENGTH }, (_, i) => (
              <CustomTextField
                key={i}
                label=""
                type="text"
                size="small"
                inputProps={{
                  maxLength: CODE_LENGTH,
                  height: 64,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  autoComplete: i === 0 ? "one-time-code" : "off",
                  style: {
                    textAlign: "center",
                    fontSize: "2rem",
                    fontWeight: 700,
                    letterSpacing: "2px",
                    padding: 0,
                    borderRadius: "12px",
                    border: "none",
                    background: "transparent",
                    outline: "none",
                    lineHeight: "64px",
                    height: "64px",
                    boxSizing: "border-box",
                  },
                }}
                value={code[i] || ""}
                inputRef={(el) => {
                  codeInputsRef.current[i] = el;
                }}
                bgColor={AUTH_FIELD_BG}
                onChange={(e) => handleCodeInput(e.target.value, i)}
                onPaste={(e) => handleCodePaste(e, i)}
                onKeyDown={(e) => handleCodeKeyDown(e, i)}
                required
                error={!!error}
                sx={{
                  width: 50,
                  minWidth: 0,
                  bgcolor: AUTH_FIELD_BG,
                  borderRadius: "12px",
                  transition: "border-color 0.2s",
                  fontWeight: 700,
                  textAlign: "center",
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                    fontSize: "2rem",
                    ml: 2,
                    fontWeight: 700,
                    padding: "12px 0",
                    letterSpacing: "2px",
                  },
                }}
              />
            ))}
          </Box>
          {error ? (
            <Typography sx={{ color: "error.main", mb: 2, fontSize: "0.88rem" }}>
              {error}
            </Typography>
          ) : (
            <Box sx={{ mb: 1 }} />
          )}
          <Button
            variant="contained"
            fullWidth
            sx={authSubmitSx}
            onClick={handleVerifyCode}
            disabled={loadingAction === "verify"}
            startIcon={spinner(loadingAction === "verify")}
          >
            {loadingAction === "verify" ? "Verifying..." : "Verify code"}
          </Button>
          <Button
            variant="text"
            fullWidth
            sx={{ mt: 1.5, textTransform: "none", fontWeight: 600, borderRadius: 2.5 }}
            onClick={handleResendCode}
            disabled={resendCooldown > 0 || loadingAction === "resend"}
          >
            {loadingAction === "resend"
              ? "Resending..."
              : resendCooldown > 0
              ? `Resend code in ${resendCooldown}s`
              : "Didn't get it? Resend code"}
          </Button>
        </AuthStagger>
      )}
      {step === 3 && (
        <AuthStagger startMs={120}>
          <AuthFieldLabel>New password</AuthFieldLabel>
          <CustomTextField
            label=""
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            bgColor={AUTH_FIELD_BG}
            autoComplete="new-password"
            onChange={(e) => setNewPassword(e.target.value)}
            required
            fullWidth
            sx={{ mb: 2 }}
            error={!!error}
            helperText={error}
            endIcon={passwordToggle(showNewPassword, () =>
              setShowNewPassword((prev) => !prev)
            )}
          />
          <AuthFieldLabel>Confirm password</AuthFieldLabel>
          <CustomTextField
            label=""
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            bgColor={AUTH_FIELD_BG}
            autoComplete="new-password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            fullWidth
            sx={{ mb: 3 }}
            error={!!error}
            helperText={error}
            endIcon={passwordToggle(showConfirmPassword, () =>
              setShowConfirmPassword((prev) => !prev)
            )}
          />
          <Button
            variant="contained"
            fullWidth
            sx={authSubmitSx}
            onClick={handleResetPassword}
            disabled={loadingAction === "reset"}
            startIcon={spinner(loadingAction === "reset")}
          >
            {loadingAction === "reset" ? "Resetting..." : "Reset password"}
          </Button>
        </AuthStagger>
      )}
      {statusMessage && (
        <Typography
          sx={{ mt: 3, color: "success.main", fontWeight: 600, textAlign: "center" }}
        >
          {statusMessage}
        </Typography>
      )}
    </AuthPage>
  );
};

export default ForgotPassword;
